import { PayoutRuleType } from '@common-fe/common-fe';
import { toString } from 'lodash';
import _ from 'lodash';

import { ServiceCategoryTypes } from '@/modules/employer/types/ServiceCategories.types';
import {
  Copay,
  CopayDto,
  GracePeriodTypeOptions,
  RunOutTypeOptions,
} from '@/modules/ExpressSetup/ExpressSetup.types.ts';
import { Plan } from '@/modules/plan/plan.types.ts';
import {
  AutoFundingConfigRenewalDto,
  CategorySpendLimitRenewalDto,
  DeductibleConfigRenewalDto,
  PaymentRuleRenewalDto,
  ProcessingTierRenewalDto,
  RenewalHealthPlanDto,
  ServiceConfigRenewalDto,
  TriggerConfigRenewalDto,
} from '@/modules/Renewals/renewals.types';

import {
  AllowRenewalsType,
  HealthPlanRenewal,
  PayrollCalendar,
  PayrollCalendarConfig,
  PayrollCalendarConfigDto,
  PayrollCalendarDto,
  Renewal,
  RenewalAutoFundingConfig,
  RenewalCategorySpendLimit,
  RenewalDeductibleConfig,
  RenewalDto,
  RenewalPaymentRule,
  RenewalPlanDto,
  RenewalPlanYear,
  RenewalPlanYearDto,
  RenewalProcessingTier,
  RenewalServiceConfig,
  RenewalServiceItem,
  RenewalTriggerConfig,
} from '../../renewals.types';

const mapPlanYear = (planYearDto: RenewalPlanYearDto): RenewalPlanYear => ({
  name: planYearDto.name,
  validFrom: planYearDto.valid_from,
  validTo: planYearDto.valid_to,
  priorPlanYearId: planYearDto.prior_plan_year_id,
});

const formatPlans = (plan: RenewalPlanDto) => {
  return {
    id: toString(plan.prior_plan_id),
    name: plan.name || '',
    type: plan.account_type,
    isCarded: plan.is_carded,
    isCustomPrefundCalc: plan.is_custom_prefund_calc,
    isPlanLevelFunding: plan.is_plan_level_funding,
    isReplenished: plan.is_replenished,
    isRollover: plan?.is_rollover,
    isAllowInvestmentsChipsDisplay: plan?.allow_investments,
    isGracePeriodChipsDisplay:
      plan?.grace_period_type === GracePeriodTypeOptions.TWO_AND_HALF_MONTH ||
      plan?.grace_period_type === GracePeriodTypeOptions.CUSTOM,
    isRunOutChipsDisplay:
      plan?.run_out_type === RunOutTypeOptions.LAST_DAY_OF_PLAN_YEAR ||
      plan?.run_out_type === RunOutTypeOptions.CUSTOM,
    isRolloverChipsDisplay: plan?.is_rollover,
    maxRolloverAmountType: plan?.max_rollover_amount_type,
    rolloverClaimsType: plan?.rollover_claims_type,
    maxRolloverAmount: plan?.max_rollover_amount,
    minRolloverAmount: plan?.min_rollover_amount,
    daysAfterPlanEnd: plan?.days_after_plan_end,
    rolloverClaimsDaysAmount: plan?.rollover_claims_days_amount,
    isAutoEnrollment: plan?.is_auto_enrollment,
    planCode: plan.plan_code,
  } as Plan;
};

const formatPaymentRules = (rules?: PaymentRuleRenewalDto[]): RenewalPaymentRule[] | undefined => {
  if (!rules) return undefined;
  return rules.map((rule) => ({
    planId: toString(rule.plan_id),
    planName: rule.plan_name,
    maxAmount: rule.max_amount,
    percentage: rule.percentage,
  }));
};

const formatServiceConfigs = (configs?: ServiceConfigRenewalDto[]): RenewalServiceConfig[] | undefined => {
  if (!configs) return undefined;
  return configs.map((config) => ({
    // @ts-ignore
    serviceName: config.service_name,
    // @ts-ignore
    services: (config.services || []).map((s: RenewalServiceItem) => ({
      id: s.id,
      name: s.name,
    })),
    claimValue: config.claim_value,
    isAppliedDeductible: config.is_applied_deductible,
    paymentType: config.payment_type,
    paymentRules: formatPaymentRules(config.payment_rules),
  }));
};

const formatProcessingTiers = (tiers?: ProcessingTierRenewalDto[]): RenewalProcessingTier[] | undefined => {
  if (!tiers) return undefined;
  return tiers.map((tier) => ({
    name: tier.name,
    displayName: tier.display_name,
    nextTierName: tier.next_tier_name,
    payoutRuleType: tier.payout_rule_type as PayoutRuleType | undefined,
    cardPurseLockPlanNames: tier.card_purse_lock_plan_names,
    serviceConfigs: formatServiceConfigs(tier.service_configs),
    tierTriggerIds: tier.tier_trigger_ids ? Array.from(tier.tier_trigger_ids) : undefined,
  }));
};

const formatTriggerConfigs = (configs?: TriggerConfigRenewalDto[]): RenewalTriggerConfig[] | undefined => {
  if (!configs) return undefined;
  return configs.map((config) => ({
    triggerCategoryType: config.trigger_category_type,
    triggerName: config.trigger_name,
    thresholdAmount: config.threshold_amount,
    triggerId: toString(config.trigger_id),
  }));
};

const formatAutoFundingConfigs = (
  configs?: AutoFundingConfigRenewalDto[]
): RenewalAutoFundingConfig[] | undefined => {
  if (!configs) return undefined;
  return configs.map((config) => ({
    planId: toString(config.plan_id),
    planName: config.plan_name,
    autoEnrollmentAmount: config.auto_enrollment_amount,
    perPersonSpendLimitAmount: config.per_person_spend_limit_amount,
    minElectionAmount: config.min_election_amount,
    maxElectionAmount: config.max_election_amount,
  }));
};

const formatCategorySpendLimits = (
  limits?: CategorySpendLimitRenewalDto[]
): RenewalCategorySpendLimit[] | undefined => {
  if (!limits) return undefined;
  return limits.map((limit) => ({
    planId: toString(limit.plan_id),
    categoryType: limit.category_type as string,
    categorySpendLimit: limit.category_spend_limit,
  }));
};

const formatDeductibleConfigs = (
  configs?: DeductibleConfigRenewalDto[]
): RenewalDeductibleConfig[] | undefined => {
  if (!configs) return undefined;
  return configs.map((config) => ({
    coverageId: toString(config.coverage_id),
    coverageName: config.coverage_name,
    coverageCode: config.coverage_code,
    triggerConfigs: formatTriggerConfigs(config.trigger_configs),
    autoFundingConfigs: formatAutoFundingConfigs(config.auto_funding_configs),
    categorySpendLimits: formatCategorySpendLimits(config.category_spend_limits),
  }));
};

const formatHealthPlans = (healthPlans?: RenewalHealthPlanDto[]): HealthPlanRenewal[] | undefined => {
  if (!healthPlans) return undefined;
  return healthPlans.map((plan) => ({
    id: toString(plan.id),
    name: plan.name,
    planCode: plan.plan_code,
    defaultName: plan.name,
    defaultPlanCode: plan.plan_code,
    processingTiers: formatProcessingTiers(plan.processing_tiers),
    deductibleConfigs: formatDeductibleConfigs(plan.deductible_configs),
    rawDto: plan,
  }));
};

const toDateString = (value?: Date | string): string | undefined =>
  value instanceof Date ? value.toISOString() : value || undefined;

const parseCalendarConfig = (config?: PayrollCalendarConfigDto): PayrollCalendarConfig | undefined => {
  if (!config) return undefined;
  return {
    calendarId: config.calendar_id,
    startDate: config.start_date ? toDateString(config.start_date) : undefined,
    frequency: config.frequency,
    dayOfWeek: config.day_of_week,
    firstDayOfMonth: config.first_day_of_month,
    secondDayOfMonth: config.second_day_of_month,
    month: config.month,
    dayOfMonth: config.day_of_month,
  };
};

const formatCalendarPlanCodes = (calendar: PayrollCalendarDto): string[] | undefined => {
  if (!calendar.calendar_plans) return undefined;
  return Array.from(calendar.calendar_plans).map((plan) => plan.plan_code || '');
};

const formatPayrollCalendars = (calendars?: PayrollCalendarDto[]): PayrollCalendar[] | undefined => {
  if (!calendars) return undefined;
  return calendars.map((calendar) => ({
    id: toString(calendar.id),
    rawDto: calendar,
    groupType: calendar.group_type,
    calendarPlanCodes: formatCalendarPlanCodes(calendar),
    name: calendar.name,
    frequencyType: calendar.frequency_type,
    schedule: calendar.schedule ? calendar.schedule.map((date) => toDateString(date) as string) : undefined,
    config: parseCalendarConfig(calendar.config),
  }));
};

export default (currentData?: RenewalDto, serviceCategories?: ServiceCategoryTypes[]) => {
  if (!currentData) {
    return undefined;
  }

  const formatCopays = (copay: CopayDto) => {
    const foundCategory = serviceCategories?.find((item) => item.key === copay.category_type);
    return {
      id: _.uniqueId(),
      amount: _.toString(copay.amount),
      categoryType: copay.category_type,
      description: foundCategory?.value || '',
    } as Copay;
  };

  const formattedPlans = currentData?.plans?.map(formatPlans) || [];
  const formattedCopays = currentData?.copays?.length ? currentData?.copays?.map(formatCopays) : [];
  const hasRolloverPlans = currentData?.plans?.some((plan) => plan.is_rollover === true) || false;

  const formattedData: Renewal = {
    id: _.toString(currentData.id),
    allowRenewalsType: currentData.allow_renewals_type as unknown as AllowRenewalsType,
    address: currentData?.addresses?.[0]
      ? {
          id: toString(currentData.addresses[0].id),
          street: currentData.addresses[0].line1 || '',
          street_2: currentData.addresses[0].line2 || '',
          city: currentData.addresses[0].city || '',
          state: currentData.addresses[0].state || '',
          zipCode: currentData.addresses[0].zipcode || '',
        }
      : undefined,
    status: currentData.status,
    planYear: mapPlanYear(currentData.plan_year),
    planYearId: _.toString(currentData.plan_year_id),
    plans: formattedPlans,
    plansDto: currentData?.plans || [],
    partnerName: currentData.partner_name,
    copays: formattedCopays,
    newPlanYear: currentData?.new_plan_year ? mapPlanYear(currentData.new_plan_year) : undefined,
    newPlanYearDto: currentData?.new_plan_year,
    logo: currentData?.dark_large_logo_path,
    privacyPolicyLink: currentData?.privacy_policy_link,
    termsAndConditionsLink: currentData?.terms_and_conditions_link,
    pricingConsentLink: currentData?.pricing_consent_link,
    orgId: _.toString(currentData.org_id),
    organizationName: currentData.org_name,
    orgPath: currentData.org_path,
    planRenewalNote: currentData?.plan_renewal_note || '',
    hasRolloverPlans,
    healthPlans: formatHealthPlans(currentData.health_plans),
    payrollCalendars: formatPayrollCalendars(currentData.payroll_calendars),
  };

  return formattedData;
};
